import type { RootState } from 'src/redux/store';
import type { IconButtonProps } from '@mui/material/IconButton';

import { m } from 'framer-motion';
import { useBoolean } from 'minimal-shared/hooks';
import { useState, useEffect, useCallback } from 'react';

import Box from '@mui/material/Box';
import Badge from '@mui/material/Badge';
import SvgIcon from '@mui/material/SvgIcon';
import Tooltip from '@mui/material/Tooltip';
import { List, Popover } from '@mui/material';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

import { paths } from 'src/routes/paths';
import { useRouter } from 'src/routes/hooks';

import { useTranslate } from 'src/locales';
import { useAppSelector } from 'src/hook/hooks';
import { useGetMediaUrlMutation } from 'src/pages/chat/slice/chatApi';
import { useCallContext } from 'src/pages/chat/hooks/use-call-context';
import { SkeletonNotificationItem } from 'src/pages/chat/components/chat-skeleton';
import {
  useGetNotificationListMutation,
  useNotificationMarkAsReadMutation,
} from 'src/pages/setting/slice/settingApi';

import { Iconify } from 'src/components/iconify';
import { Scrollbar } from 'src/components/scrollbar';
import { EmptyContent } from 'src/components/empty-content';
import { useSettingsContext } from 'src/components/settings';
import { varTap, varHover, transitionTap } from 'src/components/animate';

import { NotificationItem } from './notification-item';

import type { NotificationItemProps } from './notification-item';

export type NotificationsDrawerProps = IconButtonProps & {
  data?: NotificationItemProps['notification'][];
  loginApiData?: any;
};

export function NotificationsDrawer({ data = [], sx, ...other }: NotificationsDrawerProps) {
  const { value: open, onFalse: onClose, onTrue: onOpen } = useBoolean();
  const { loginApiData } = useAppSelector((state: RootState) => state.auth);

  const [getNotificationList] = useGetNotificationListMutation();
  const [notificationMarkAsRead] = useNotificationMarkAsReadMutation();
  const [getMediaUrl] = useGetMediaUrlMutation();
  const { handleNotification } = useCallContext();
  const settings = useSettingsContext();
  const { t } = useTranslate('translation');
  const router = useRouter();

  const [currentTab, setCurrentTab] = useState('all');

  const handleChangeTab = useCallback((event: React.SyntheticEvent, newValue: string) => {
    setCurrentTab(newValue);
  }, []);

  const [notifications, setNotifications] = useState<any[]>([]);
  const [totalPages, setTotalPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  // const totalUnRead = notifications.filter((item) => item?.isUnRead === true).length;

  const handleMarkAllAsRead = async () => {
    if (!loginApiData || !('id' in loginApiData)) {
      console.error('loginApiData or loginApiData.id is undefined');
      return;
    }
    const payload = {
      userId: 'id' in loginApiData && loginApiData.id,
      markAsReadAll: true,
    };
    await notificationMarkAsRead(payload).unwrap();
    fetchNotifications(1);
  };

  const handleClickNotification = async (selectedNotification: any) => {
    router.replace(paths.chat.root);
    onClose();
    handleNotification(selectedNotification?.primaryActorId);
    if (!selectedNotification.isRead) {
      const payload = {
        id: selectedNotification?.id,
      };
      await notificationMarkAsRead(payload).unwrap();
      fetchNotifications(1);
    }
  };

  const renderHead = () => (
    <Box
      sx={{
        py: 2,
        pr: 1,
        pl: 2.5,
        minHeight: 68,
        display: 'flex',
        alignItems: 'center',
      }}
    >
      <Typography variant="h6" sx={{ flexGrow: 1 }}>
        {t('NOTIFICATIONS')}
      </Typography>
      <IconButton
        color="primary"
        onClick={handleMarkAllAsRead}
        disabled={notifications?.filter((item) => !item?.isRead)?.length === 0}
      >
        <Tooltip title={t('MARK_ALL_AS_READ')}>
          <Iconify icon="eva:done-all-fill" />
        </Tooltip>
      </IconButton>

      <IconButton onClick={onClose}>
        <Iconify icon="mingcute:close-line" />
      </IconButton>
    </Box>
  );

  const renderList = () => (
    <Scrollbar>
      <Box component="ul">
        {[...notifications]
          ?.sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime())
          ?.map((notification, index) => (
            <Box component="li" key={index} sx={{ display: 'flex' }}>
              <NotificationItem
                notification={notification}
                handleClickNotification={() => handleClickNotification(notification)}
              />
            </Box>
          ))}
      </Box>
    </Scrollbar>
  );
  // ==============================================================================================================================================
  const profileUrlCache = new Map<string, string>(); // userId => final profile URL
  const profileUrlPromises = new Map<string, Promise<string>>(); // userId => Promise<string>

  const resolveProfileUrl = async (
    rawProfile: string | undefined,
    userId: string
  ): Promise<string> => {
    if (!rawProfile || typeof rawProfile !== 'string') return '';

    if (profileUrlCache.has(userId)) {
      return profileUrlCache.get(userId)!;
    }

    let profileUrlPromise = profileUrlPromises.get(userId);
    if (!profileUrlPromise) {
      profileUrlPromise = (async () => {
        try {
          if (rawProfile.startsWith('http')) {
            return rawProfile;
          }
          const { data: fileUrl } = await getMediaUrl({ fileName: rawProfile });
          return fileUrl;
        } catch {
          return rawProfile;
        }
      })();
      profileUrlPromises.set(userId, profileUrlPromise);
    }

    const finalUrl = await profileUrlPromise;
    profileUrlCache.set(userId, finalUrl);
    return finalUrl;
  };
  const fetchNotifications = async (pageNum: number) => {
    if (loading) return;
    setLoading(true);
    try {
      const response = await getNotificationList({
        filters: [
          { fieldname: 'createdAt', sort: 1 },
          // {
          //   fieldname: 'isRead',
          //   search: false,
          // },
          {
            fieldname: 'userId',
            search: loginApiData && 'id' in loginApiData && loginApiData.id,
          },
        ],
        globalSearch: '',
        page: pageNum,
        limit: 10,
      }).unwrap();

      const notificationsres = Array.isArray(response?.list) ? response.list : [];

      const enrichedPayloads = await Promise.all(
        notificationsres.map(async (n: any) => {
          const userId = String(n.primaryActorId || n.id || 'unknown');
          const rawProfile = n?.meta?.conversation?.contact?.ProfileURL;
          const profileUrl = await resolveProfileUrl(rawProfile, userId);
          return { ...n, profileUrl };
        })
      );

      setNotifications((prev) => {
        if (pageNum === 1) {
          // First page: reset the notifications list
          return enrichedPayloads;
        } else {
          // Subsequent pages: merge without duplicates
          const ids = new Set(prev.map((n: any) => n.id));
          const newItems = enrichedPayloads.filter((n: any) => !ids.has(n.id));
          return [...prev, ...newItems];
        }
      });

      setTotalPages(response.totalPages);
      setPage(pageNum);
    } catch (error) {
      console.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleScrollNotification = useCallback(
    (event: React.UIEvent<HTMLDivElement>) => {
      const { scrollTop, scrollHeight, clientHeight } = event.currentTarget;
      setTotalPages((prev) => prev);
      if (loading || page >= totalPages) return;

      if (scrollTop + clientHeight >= scrollHeight - 1) {
        setLoading(true);
        fetchNotifications(page + 1);
      }
    },
    [loading, page, totalPages]
  );

  // ==============================================================================================================================================

  useEffect(() => {
    const handleMessage = async (event: MessageEvent) => {
      if (!event.data || typeof event.data !== 'object') return;

      const { type, payload } = event.data as { type: string; payload: any };

      if (type === 'NOTIFICATION_UPSERT') {
        const userId = String(payload.primaryActorId || payload.id || 'unknown');
        const rawProfile = payload?.meta?.conversation?.contact?.ProfileURL;

        const profileUrl = await resolveProfileUrl(rawProfile, userId);

        const enrichedPayload = {
          ...payload,
          primaryActor: {
            ...payload.primaryActor,
            ProfileURL: profileUrl,
          },
        };

        setNotifications((prev: any[]) => {
          const exists = prev.some((n: any) => n?.id === enrichedPayload?.id);
          const updated = exists
            ? prev.map((n: any) =>
                n?.primaryActorId === enrichedPayload?.primaryActorId &&
                n?.id === enrichedPayload?.id
                  ? { ...enrichedPayload }
                  : n
              )
            : [...prev, enrichedPayload];
          return updated;
        });
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  useEffect(() => {
    if (notifications?.length === 0 && loginApiData) {
      fetchNotifications(1);
    }
  }, [notifications.length, loginApiData]);

  return (
    <>
      <Tooltip title={t('NOTIFICATIONS')} placement="bottom" arrow>
        <IconButton
          component={m.button}
          whileTap={varTap(0.96)}
          whileHover={varHover(1.04)}
          transition={transitionTap()}
          aria-label="Notifications button"
          onClick={onOpen}
          sx={sx}
          {...other}
        >
          <Badge
            badgeContent={notifications?.filter((item) => !item?.isRead)?.length}
            color="error"
          >
            <SvgIcon>
              <path
                fill="currentColor"
                d="M18.75 9v.704c0 .845.24 1.671.692 2.374l1.108 1.723c1.011 1.574.239 3.713-1.52 4.21a25.794 25.794 0 0 1-14.06 0c-1.759-.497-2.531-2.636-1.52-4.21l1.108-1.723a4.393 4.393 0 0 0 .693-2.374V9c0-3.866 3.022-7 6.749-7s6.75 3.134 6.75 7"
                opacity="0.5"
              />
              <path
                fill="currentColor"
                d="M12.75 6a.75.75 0 0 0-1.5 0v4a.75.75 0 0 0 1.5 0zM7.243 18.545a5.002 5.002 0 0 0 9.513 0c-3.145.59-6.367.59-9.513 0"
              />
            </SvgIcon>
          </Badge>
        </IconButton>
      </Tooltip>
      <Popover
        open={open}
        onClose={onClose}
        anchorEl={document.body}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: settings.state.direction === 'ltr' ? 'right' : 'left',
        }}
        slotProps={{
          paper: {
            sx: {
              width: '100%',
              maxWidth: 360,
              ml: 'auto',
              height: '80vh',
              maxHeight: '80vh',
              top: '7% !important',
              backgroundColor: 'background.default',
              direction: settings.state.direction,
              ...(settings.state.direction === 'ltr' ? { left: '20px' } : { right: '20px' }),
            },
          },
        }}
      >
        {renderHead()}

        <Box
          sx={{
            height: '65vh',
            overflowY: 'auto',
          }}
          // onScroll={handleScrollNotification}
        >
          {renderList()}

          {loading && (
            <Box sx={{ bgcolor: '#fff', borderRadius: 2 }}>
              <List>
                {Array.from(new Array(5)).map((_, index) => (
                  <SkeletonNotificationItem key={index} />
                ))}
              </List>
            </Box>
          )}

          {notifications?.length === 0 && !loading && (
            <Box
              sx={{
                flexGrow: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '100%',
              }}
            >
              <EmptyContent title="You have no new notifications." />
            </Box>
          )}
        </Box>

        {/* <Box sx={{ p: 1 }}>
          <Button fullWidth size="large">
            View all
          </Button>
        </Box> */}
      </Popover>
    </>
  );
}
